import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getDailyReportVisibleMembers,
  updateDailyReportVisibleMembers
} from '../../../api/departments';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { useToast } from '../../common/Toast';
import Avatar from '../../common/Avatar';
import { getRoleLabel } from '../../../utils/roleUtils';
import './MemberVisibilityModal.scss';

const MemberVisibilityModal = ({ isOpen, onClose, onSuccess, departmentId: propDepartmentId }) => {
  const { profile } = useAuth();
  const departmentId = propDepartmentId || profile?.department_id;
  const { companyUsers: companyUsersList } = useBetaData();
  const [allMembers, setAllMembers] = useState([]);
  const [orderedMemberIds, setOrderedMemberIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, companyUsersList, departmentId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 該部門成員 + 沒有 department_id 的成員
      const members = departmentId
        ? companyUsersList.filter(u => u.department_id === departmentId || !u.department_id)
        : companyUsersList;
      setAllMembers(members);

      // 載入當前設定（保持順序）
      const { data: visibleIds, error: visibleError } = await getDailyReportVisibleMembers(departmentId);
      if (visibleError) throw visibleError;

      setOrderedMemberIds(visibleIds || []);
    } catch (error) {
      console.error('載入失敗:', error);
      toast.error('載入失敗：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMember = (memberId) => {
    if (orderedMemberIds.includes(memberId)) {
      // 取消勾選：從陣列中移除
      setOrderedMemberIds(orderedMemberIds.filter(id => id !== memberId));
    } else {
      // 勾選：加到陣列最後
      setOrderedMemberIds([...orderedMemberIds, memberId]);
    }
  };

  // 上移成員
  const moveUp = (memberId) => {
    const index = orderedMemberIds.indexOf(memberId);
    if (index > 0) {
      const newOrder = [...orderedMemberIds];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setOrderedMemberIds(newOrder);
    }
  };

  // 下移成員
  const moveDown = (memberId) => {
    const index = orderedMemberIds.indexOf(memberId);
    if (index < orderedMemberIds.length - 1 && index !== -1) {
      const newOrder = [...orderedMemberIds];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setOrderedMemberIds(newOrder);
    }
  };

  // 全選/全不選
  const toggleAll = (selectAll) => {
    if (selectAll) {
      setOrderedMemberIds(allMembers.map(m => m.id));
    } else {
      setOrderedMemberIds([]);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const { error } = await updateDailyReportVisibleMembers(orderedMemberIds, departmentId);
      if (error) throw error;
      
      toast.success('設定已儲存！');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('儲存失敗:', error);
      toast.error('儲存失敗：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 過濾搜尋並排序（已選擇的在上面，並按照順序排列）
  const filteredMembers = useMemo(() => {
    let members = allMembers;
    
    // 搜尋過濾
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      members = members.filter(member => 
        member.name?.toLowerCase().includes(query) ||
        member.email?.toLowerCase().includes(query)
      );
    }
    
    // 排序：已選擇的按照 orderedMemberIds 順序在前，未選擇的在後
    const selectedMembers = orderedMemberIds
      .map(id => members.find(m => m.id === id))
      .filter(m => m); // 過濾掉找不到的
    
    const unselectedMembers = members.filter(
      m => !orderedMemberIds.includes(m.id)
    );
    
    return [...selectedMembers, ...unselectedMembers];
  }, [allMembers, searchQuery, orderedMemberIds]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content member-visibility-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ 管理 Daily Report 顯示成員</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-description">
          <p>選擇要在 Daily Report 中顯示的團隊成員。</p>
          <p>只有勾選的成員會出現在 Daily Report 列表中。</p>
        </div>

        <div className="modal-toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="🔍 搜尋成員姓名或 Email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="toolbar-actions">
            <button
              className="toggle-all-btn"
              onClick={() => toggleAll(true)}
              type="button"
            >
              ☑ 全選
            </button>
            <button
              className="toggle-all-btn"
              onClick={() => toggleAll(false)}
              type="button"
            >
              ☐ 全不選
            </button>
            <span className="selected-count">
              已選擇: {orderedMemberIds.length} / {allMembers.length} 位
            </span>
          </div>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">載入中...</div>
          ) : filteredMembers.length === 0 ? (
            <div className="empty-state">
              {searchQuery ? '沒有符合的成員' : '目前沒有可選擇的成員'}
            </div>
          ) : (
            <div className="members-list">
              {filteredMembers.map((member) => {
                const isSelected = orderedMemberIds.includes(member.id);
                const selectedIndex = orderedMemberIds.indexOf(member.id);
                const isFirst = selectedIndex === 0;
                const isLast = selectedIndex === orderedMemberIds.length - 1;
                
                return (
                  <div
                    key={member.id}
                    className={`member-item ${isSelected ? 'selected' : ''}`}
                  >
                    <label className="member-checkbox-label">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleMember(member.id)}
                      />
                      <Avatar
                        avatarUrl={member.avatar_url}
                        name={member.name}
                        size="small"
                        className="member-avatar"
                      />
                      
                      <div className="member-info">
                        <div className="member-name">{member.name || member.email || '未命名'}</div>
                        <div className="member-email">{member.email}</div>
                      </div>
                      
                      <div className="member-role-badge">
                        {getRoleLabel(member.role)}
                      </div>
                    </label>
                    
                    {isSelected && (
                      <div className="sort-buttons">
                        <button
                          className="sort-btn"
                          onClick={() => moveUp(member.id)}
                          disabled={isFirst}
                          title="上移"
                        >
                          ▲
                        </button>
                        <button
                          className="sort-btn"
                          onClick={() => moveDown(member.id)}
                          disabled={isLast}
                          title="下移"
                        >
                          ▼
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            取消
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? '儲存中...' : '儲存設定'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberVisibilityModal;

